import { type Server, ServersService } from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import {
  connectMagicNotesServer,
  findMagicNotesServer,
} from '@affine/core/modules/cloud/magicnotes';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { MagicNotesSignIn } from './magicnotes-sign-in';

export type SignInStep =
  | 'signIn'
  | 'signInWithPassword'
  | 'signInWithEmail'
  | 'addSelfhosted';

export interface SignInState {
  step: SignInStep;
  server?: Server;
  initialServerBaseUrl?: string;
  email?: string;
  hasPassword?: boolean;
  redirectUrl?: string;
}

export const SignInPanel = ({
  onSkip,
  onAuthenticated,
  initialEmail,
  initialSetup,
  initialForgotPassword,
  requirePasswordSignIn,
  initialToken,
  redirectUrl,
}: {
  onAuthenticated?: (status: AuthSessionStatus) => void;
  onSkip: () => void;
  server?: string;
  initStep?: SignInStep | undefined;
  redirectUrl?: string;
  initialEmail?: string;
  initialSetup?: boolean;
  initialForgotPassword?: boolean;
  requirePasswordSignIn?: boolean;
  initialToken?: string;
}) => {
  const serversService = useService(ServersService);
  const [server, setServer] = useState(() =>
    findMagicNotesServer(serversService)
  );
  const [connectionError, setConnectionError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    let active = true;
    setConnectionError(false);
    connectMagicNotesServer(serversService)
      .then(server => {
        if (active) setServer(server);
      })
      .catch(() => {
        if (active) setConnectionError(true);
      });
    return () => {
      active = false;
    };
  }, [serversService, attempt]);

  const form = (
    <MagicNotesSignIn
      server={server}
      initialEmail={initialEmail}
      initialSetup={initialSetup}
      initialForgotPassword={initialForgotPassword}
      requirePasswordSignIn={requirePasswordSignIn}
      initialToken={initialToken}
      redirectUrl={redirectUrl}
      connectionError={connectionError}
      onRetry={retry}
      onSkip={onSkip}
      onAuthenticated={onAuthenticated}
    />
  );

  return form;
};
